import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../utils/api.js';
import { API_ENDPOINTS, STORAGE_KEYS } from '../config/api.js';
import { setAuthToken, removeAuthToken, getAuthToken } from '../utils/api.js';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  // SSR guard: Check if we're in a browser environment
  const isBrowser = typeof window !== 'undefined';
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load user on mount if token exists (client-side only)
  useEffect(() => {
    if (isBrowser) {
      loadUser();
    } else {
      setLoading(false);
    }
  }, [isBrowser]);

  async function loadUser() {
    if (!isBrowser) {
      setLoading(false);
      return;
    }
    
    const token = getAuthToken();
    
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get(API_ENDPOINTS.ME);
      setUser(response.user);
      if (isBrowser) {
        localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(response.user));
      }
    } catch (error) {
      console.error('Failed to load user:', error);
      removeAuthToken();
    } finally {
      setLoading(false);
    }
  }

  async function login(email, password) {
    setError(null);
    
    try {
      const response = await api.post(API_ENDPOINTS.LOGIN, { email, password });
      
      setAuthToken(response.token);
      setUser(response.user);
      if (isBrowser) {
        localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(response.user));
      }
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.data?.error || error.message || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }

  async function register(email, password, timezone) {
    setError(null);
    
    try {
      const response = await api.post(API_ENDPOINTS.REGISTER, { 
        email, 
        password,
        timezone
      });
      
      setAuthToken(response.token);
      setUser(response.user);
      if (isBrowser) {
        localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(response.user));
      }
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.data?.error || error.message || 'Registration failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }

  async function logout() {
    removeAuthToken();
    setUser(null);
    setError(null);
  }

  async function updateProfile(updates) {
    setError(null);
    
    try {
      const response = await api.put(API_ENDPOINTS.UPDATE_PROFILE, updates);
      setUser(response.user);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(response.user));
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.data?.error || error.message || 'Update failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }

  async function updateEducationFlags(flags) {
    try {
      const response = await api.put('/api/auth/education-flags', { flags });
      // Update local user object with new flags
      const updatedUser = {
        ...user,
        metadata: {
          ...user.metadata,
          educationFlags: response.educationFlags
        }
      };
      setUser(updatedUser);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(updatedUser));
      
      return { success: true };
    } catch (error) {
      console.error('Failed to update education flags:', error);
      return { success: false, error: error.message };
    }
  }

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    updateProfile,
    updateEducationFlags,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

